/**
 * Age verification gate for alcohol products.
 *
 * Behavior:
 *  - Shows a modal on pages that involve alcohol (PDP for tagged products,
 *    PLP/search/etc when product cards with [data-alcohol="true"] are present,
 *    or when <meta name="page-has-alcohol"> is set).
 *  - Stores consent in localStorage with a 30-day TTL.
 *  - On cart page/drawer, blocks checkout button(s) until the 20+ checkbox
 *    is ticked (the checkbox snippet is rendered by age-gate-cart.liquid
 *    only when the cart actually contains alcohol items).
 *  - "No" button redirects to the homepage.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'age_verified_20';
  var TTL_DAYS = 30;
  var TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

  function isVerified() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      var ts = parseInt(raw, 10);
      if (!ts || isNaN(ts)) return false;
      return (Date.now() - ts) < TTL_MS;
    } catch (e) {
      return false;
    }
  }

  function setVerified() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) { /* storage unavailable; modal will reappear next visit */ }
  }

  function pageInvolvesAlcohol() {
    return !!document.querySelector('meta[name="page-has-alcohol"][content="true"]');
  }

  // ---------- Modal ----------
  function initModal() {
    var modal = document.getElementById('AgeGate');
    if (!modal) return;

    var panel = modal.querySelector('.age-gate__panel');
    var yesBtn = modal.querySelector('[data-age-gate-yes]');
    var noBtn = modal.querySelector('[data-age-gate-no]');
    var overlay = modal.querySelector('[data-age-gate-overlay]');
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('age-gate-open');
      // Focus the panel for screen readers; primary button for keyboard users.
      if (yesBtn) yesBtn.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      modal.hidden = true;
      document.documentElement.classList.remove('age-gate-open');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try { lastFocus.focus(); } catch (e) { /* ignore */ }
      }
    }

    function onKeydown(e) {
      if (e.key === 'Tab') {
        // Trap focus between yes / no buttons.
        var focusables = [yesBtn, noBtn].filter(Boolean);
        if (focusables.length === 0) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      // Intentionally NOT closing on ESC: legal gate must require explicit answer.
    }

    if (yesBtn) {
      yesBtn.addEventListener('click', function () {
        setVerified();
        close();
      });
    }
    if (noBtn) {
      noBtn.addEventListener('click', function () {
        // Send underage / declining users away from the alcohol context.
        window.location.href = '/';
      });
    }
    // Overlay clicks intentionally do NOT close — explicit choice required.
    if (overlay) {
      overlay.addEventListener('click', function (e) { e.stopPropagation(); });
    }

    if (pageInvolvesAlcohol() && !isVerified()) {
      open();
    }
  }

  // ---------- Cart confirmation ----------
  function initCartGate() {
    var wrappers = document.querySelectorAll('[data-cart-has-alcohol="true"]:not([data-age-gate-bound])');
    if (!wrappers.length) return;

    wrappers.forEach(function (wrapper) {
      wrapper.setAttribute('data-age-gate-bound', 'true');
      var checkbox = wrapper.querySelector('[data-age-gate-cart-checkbox]');
      var error = wrapper.querySelector('.age-gate-cart__error');
      var ctx = wrapper.getAttribute('data-context') || 'page';

      // Match checkout button by context.
      var checkoutBtn = ctx === 'drawer'
        ? document.getElementById('CartDrawer-Checkout')
        : document.getElementById('checkout');

      // Express checkout buttons (Apple Pay etc.) — same parent area.
      var expressContainer = ctx === 'drawer'
        ? null
        : document.querySelector('.cart__dynamic-checkout-buttons');

      function applyState() {
        var ok = checkbox && checkbox.checked;
        if (checkoutBtn) {
          if (ok) {
            checkoutBtn.removeAttribute('data-age-gate-blocked');
          } else {
            checkoutBtn.setAttribute('data-age-gate-blocked', 'true');
          }
        }
        if (expressContainer) {
          expressContainer.classList.toggle('age-gate-blocked', !ok);
        }
        if (ok && error) error.hidden = true;
      }

      // Expose for re-evaluation on pageshow / bfcache restore.
      wrapper._ageGateApplyState = applyState;

      if (checkbox) {
        checkbox.addEventListener('change', applyState);
      }

      // Intercept clicks on the checkout button when blocked.
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', function (e) {
          if (checkoutBtn.getAttribute('data-age-gate-blocked') === 'true') {
            e.preventDefault();
            e.stopPropagation();
            if (error) error.hidden = false;
            if (checkbox) checkbox.focus();
          }
        }, true);
      }

      // Block express checkout container clicks when not consented.
      if (expressContainer) {
        expressContainer.addEventListener('click', function (e) {
          if (expressContainer.classList.contains('age-gate-blocked')) {
            e.preventDefault();
            e.stopPropagation();
            if (error) error.hidden = false;
            if (checkbox) checkbox.focus();
          }
        }, true);
      }

      applyState();
    });
  }

  function init() {
    initModal();
    initCartGate();
  }

  // Re-bind the cart gate when the cart section is re-rendered
  // (drawer ajax updates, cart page section refresh, cart notification, etc.).
  function watchForCartUpdates() {
    if (typeof MutationObserver === 'undefined') return;
    var observer = new MutationObserver(function () {
      if (document.querySelector('[data-cart-has-alcohol="true"]:not([data-age-gate-bound])')) {
        initCartGate();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // On bfcache restore (browser back/forward), DOM is preserved but scripts
  // don't re-run. The checkbox may show as checked while the checkout button
  // still has the stale [data-age-gate-blocked] attribute. Re-evaluate state.
  function reEvaluateOnPageShow(e) {
    if (!e.persisted) return;
    document.querySelectorAll('[data-cart-has-alcohol="true"]').forEach(function (wrapper) {
      if (typeof wrapper._ageGateApplyState === 'function') {
        wrapper._ageGateApplyState();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      watchForCartUpdates();
    });
  } else {
    init();
    watchForCartUpdates();
  }
  window.addEventListener('pageshow', reEvaluateOnPageShow);
})();